import { motion } from 'framer-motion';
import { BarChart3, Monitor, Sparkles, TrendingUp, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface AnalyticsLoadingProps {
  onMinimumDurationComplete?: () => void;
}

const AnalyticsLoading: React.FC<AnalyticsLoadingProps> = ({ onMinimumDurationComplete }) => {
  // Progress state management
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);

  // Adjusted durations to total 5.7 seconds (5700ms)
  const steps = [
    { text: 'Fetching dashboard metrics...', duration: 1800 },
    { text: 'Processing analytics data...', duration: 2100 },
    { text: 'Building visualizations...', duration: 1800 },
  ];

  // Dynamic color system based on progress
  const getProgressColors = (progress: number) => {
    if (progress <= 33) {
      // Red phase (0-33%)
      return {
        gradient: 'from-red-500 via-red-400 to-orange-500',
        glow: 'from-red-400 via-red-300 to-orange-400',
        border: 'border-red-500',
        dot: 'bg-red-500',
      };
    } else if (progress <= 66) {
      // Orange phase (34-66%)
      return {
        gradient: 'from-orange-500 via-amber-500 to-yellow-500',
        glow: 'from-orange-400 via-amber-400 to-yellow-400',
        border: 'border-orange-500',
        dot: 'bg-orange-500',
      };
    } else {
      // Green phase (67-100%)
      return {
        gradient: 'from-yellow-500 via-green-500 to-emerald-500',
        glow: 'from-yellow-400 via-green-400 to-emerald-400',
        border: 'border-green-500',
        dot: 'bg-green-500',
      };
    }
  };

  const colors = getProgressColors(progress);

  // Minimum duration timer (5.7 seconds)
  useEffect(() => {
    const minimumTimer = setTimeout(() => {
      setMinimumDurationComplete(true);
      onMinimumDurationComplete?.();
    }, 5700); // 5.7 seconds

    return () => clearTimeout(minimumTimer);
  }, [onMinimumDurationComplete]);

  // Animate progress through steps
  useEffect(() => {
    let stepTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;

    const animateStep = (stepIndex: number) => {
      if (stepIndex >= steps.length) {
        // Stay at completion
        setCurrentStep(steps.length - 1);
        setProgress(100);

        return;
      }

      setCurrentStep(stepIndex);
      const stepDuration = steps[stepIndex].duration;
      const startProgress = (stepIndex / steps.length) * 100;
      const endProgress = ((stepIndex + 1) / steps.length) * 100;

      // Animate progress bar for this step
      const startTime = Date.now();

      const animateProgress = () => {
        const elapsed = Date.now() - startTime;
        const stepProgress = Math.min(elapsed / stepDuration, 1);
        const currentProgress = startProgress + (endProgress - startProgress) * stepProgress;

        setProgress(currentProgress);

        if (stepProgress < 1) {
          progressTimer = setTimeout(animateProgress, 16); // 60fps
        } else {
          stepTimer = setTimeout(() => animateStep(stepIndex + 1), 200);
        }
      };

      animateProgress();
    };

    animateStep(0);

    return () => {
      clearTimeout(stepTimer);
      clearTimeout(progressTimer);
    };
  }, []);

  // Lock all scrolling when component mounts
  useEffect(() => {
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    // Also lock html element scroll
    document.documentElement.style.overflow = 'hidden';
    // Prevent scroll on the body
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    // Prevent scroll events
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      return false;
    };

    // Add event listeners to prevent scrolling
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', e => {
      // Prevent arrow keys, page up/down, space, home, end
      if ([32, 33, 34, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) > -1) {
        e.preventDefault();
      }
    });

    // Cleanup: restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';

      // Remove event listeners
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('keydown', preventScroll);
    };
  }, []);

  return (
    <div className="h-screen relative overflow-hidden bg-white">
      {/* Clean subtle background */}
      <div className="absolute inset-0 bg-gray-50/30">
        {/* Subtle floating elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-16 h-16 bg-blue-100/40 rounded-full blur-xl"
          animate={{
            y: [0, -15, 0],
            x: [0, 8, 0],
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0,
          }}
        />
        <motion.div
          className="absolute top-1/2 right-1/3 w-12 h-12 bg-indigo-100/40 rounded-full blur-xl"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
            scale: [1, 0.9, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/2 w-20 h-20 bg-emerald-100/30 rounded-full blur-xl"
          animate={{
            y: [0, -12, 0],
            x: [0, 6, 0],
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>

      {/* Main Loading Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-screen p-6">
        {/* Clean Loading Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{
            opacity: 1,
            scale: [1, 1.01, 1],
            y: 0,
          }}
          transition={{
            opacity: { duration: 0.6, ease: 'easeOut' },
            y: { duration: 0.6, ease: 'easeOut' },
            scale: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
          }}
          className="bg-white/90 backdrop-blur-md rounded-3xl border border-gray-200/60 p-8 shadow-2xl max-w-lg w-full mx-auto ring-1 ring-white/20"
        >
          {/* Header Section */}
          <div className="text-center mb-6">
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: { duration: 3, repeat: Infinity, ease: 'linear' },
                scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
              }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4 shadow-lg"
            >
              <BarChart3 className="w-8 h-8 text-white" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-3xl font-black text-gray-800 mb-2"
            >
              Loading Analytics
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-lg text-gray-600 font-medium"
            >
              Preparing your insights...
            </motion.p>
          </div>

          {/* Premium Progress Bar */}
          <div className="w-full max-w-md mx-auto mb-8">
            {/* Current Step Text */}
            <motion.div
              className="text-center mb-4"
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-gray-700 text-sm font-medium">{steps[currentStep]?.text}</span>
              <div className="text-right mt-1">
                <motion.span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-mono shadow-sm border transition-all duration-300 ${
                    progress <= 33
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : progress <= 66
                        ? 'bg-orange-50 text-orange-700 border-orange-200'
                        : 'bg-green-50 text-green-700 border-green-200'
                  }`}
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  key={Math.floor(progress / 10)} // Re-trigger animation every 10%
                >
                  <motion.span
                    animate={{
                      opacity: [0.8, 1, 0.8],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    {Math.round(progress)}%
                  </motion.span>

                  {/* Completion sparkle effect */}
                  {progress > 95 && (
                    <motion.span
                      className="ml-1 text-green-500"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      ✨
                    </motion.span>
                  )}
                </motion.span>
              </div>
            </motion.div>

            {/* Progress Bar Container */}
            <div className="relative w-full h-5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 to-gray-300"></div>

              {/* Animated Progress Fill */}
              <motion.div
                className={`absolute top-0 left-0 h-full bg-gradient-to-r ${colors.gradient} rounded-full shadow-lg`}
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>

                {/* Glow Effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${colors.glow} blur-sm opacity-60`}
                ></div>
              </motion.div>

              {/* Progress Head Glow */}
              {progress > 0 && (
                <motion.div
                  className={`absolute top-1/2 w-4 h-4 -mt-2 bg-white rounded-full shadow-lg border-2 ${colors.border}`}
                  style={{ left: `calc(${progress}% - 8px)` }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <div className={`absolute inset-1 ${colors.dot} rounded-full`}></div>
                </motion.div>
              )}

              {/* Finish Line Target */}
              <motion.div
                className="absolute top-1/2 -mt-3 right-0 transform translate-x-2"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                {/* Outer Ring */}
                <div className="relative w-6 h-6 rounded-full border-2 border-emerald-400 bg-white/90 shadow-lg">
                  {/* Inner Dot */}
                  <div className="absolute inset-1 bg-emerald-500 rounded-full"></div>

                  {/* Animated Rings */}
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-emerald-300"
                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                  />
                  <motion.div
                    className="absolute inset-0 rounded-full border border-emerald-200"
                    animate={{ scale: [1, 2, 1], opacity: [0.8, 0, 0.8] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 0.5 }}
                  />
                </div>

                {/* Success Checkmark (appears when near completion) */}
                {progress > 85 && (
                  <motion.div
                    className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full flex items-center justify-center"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  >
                    <svg
                      className="w-2 h-2 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.div>
            </div>

            {/* Step Indicators */}
            <div className="flex justify-between mt-3 px-1">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-500 ${
                    index <= currentStep ? colors.dot : 'bg-gray-300'
                  }`}
                  animate={index === currentStep ? { scale: [1, 1.3, 1] } : {}}
                  transition={{ duration: 0.5, repeat: index === currentStep ? Infinity : 0 }}
                />
              ))}
            </div>
          </div>

          {/* Skeleton Cards Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                className="bg-gray-50/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-200/50"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <motion.div
                    animate={{
                      backgroundColor: [
                        'rgba(156, 163, 175, 0.3)',
                        'rgba(156, 163, 175, 0.5)',
                        'rgba(156, 163, 175, 0.3)',
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.2,
                    }}
                    className="w-8 h-8 rounded-lg"
                  />
                  <div className="flex-1 space-y-2">
                    <motion.div
                      animate={{
                        backgroundColor: [
                          'rgba(156, 163, 175, 0.3)',
                          'rgba(156, 163, 175, 0.5)',
                          'rgba(156, 163, 175, 0.3)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.2 + 0.1,
                      }}
                      className="h-3 rounded"
                    />
                    <motion.div
                      animate={{
                        backgroundColor: [
                          'rgba(156, 163, 175, 0.3)',
                          'rgba(156, 163, 175, 0.5)',
                          'rgba(156, 163, 175, 0.3)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.2 + 0.2,
                      }}
                      className="h-2 rounded w-3/4"
                    />
                  </div>
                </div>
                <motion.div
                  animate={{
                    backgroundColor: [
                      'rgba(156, 163, 175, 0.3)',
                      'rgba(156, 163, 175, 0.5)',
                      'rgba(156, 163, 175, 0.3)',
                    ],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.2 + 0.3,
                  }}
                  className="h-8 rounded mb-2"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Icons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="flex items-center space-x-8 mt-8"
        >
          {[
            { icon: Users, delay: 0, color: 'bg-blue-100' },
            { icon: TrendingUp, delay: 0.2, color: 'bg-emerald-100' },
            { icon: Monitor, delay: 0.4, color: 'bg-indigo-100' },
            { icon: Sparkles, delay: 0.6, color: 'bg-purple-100' },
          ].map(({ icon: Icon, delay, color }, index) => (
            <motion.div
              key={index}
              animate={{
                y: [0, -6, 0],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay,
              }}
              className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center border border-gray-200/50 shadow-sm`}
            >
              <Icon className="w-6 h-6 text-gray-600" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default AnalyticsLoading;
